import { Check, ChevronDown, ChevronLeft, Search, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { remainingLabel, urgencyColor } from "@/lib/format";
import { LIVE_OFF_LABEL, useLiveOff } from "@/lib/live";
import type { BoardDetail } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The slim bar above a view in the tab layout. It names the view and, on a board,
 * turns into the way around boards: a back chevron to the Boards list, and the
 * title as a switcher, so another board is two taps away without leaving the tab.
 */
export function TopBar({
  title,
  live,
  board,
  boards,
  now,
  onBack,
  onSwitchBoard,
  onAllBoards,
  onSearch,
  actions,
}: {
  title: string;
  live: boolean;
  /** The open board, when the view is one — makes the title a switcher. */
  board: BoardDetail | null;
  boards: BoardDetail[];
  now: number;
  /** Shown as a back chevron when present. */
  onBack?: () => void;
  onSwitchBoard: (boardId: string) => void;
  onAllBoards: () => void;
  /** Opens the command palette; a search button left of the view's actions. */
  onSearch?: () => void;
  /** The current view's own actions, at the right. */
  actions?: React.ReactNode;
}) {
  const liveOff = useLiveOff();
  return (
    <div className="flex shrink-0 items-center gap-1 border-b border-border/70 bg-surface/60 px-2 pb-1.5 pt-[max(0.375rem,env(safe-area-inset-top))]">
      {onBack ? (
        <Button variant="ghost" size="icon" aria-label="All boards" onClick={onBack} className="-mr-1">
          <ChevronLeft className="size-5" />
        </Button>
      ) : (
        <span className="w-2" />
      )}
      {board ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex min-w-0 items-center gap-1 rounded-md px-1.5 py-1 text-left active:bg-muted/60"
              aria-label={`${title} — switch board`}
            >
              <span className="truncate text-[14px] font-semibold tracking-tight">{title}</span>
              <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[60dvh] w-72 overflow-y-auto">
            <DropdownMenuLabel>Switch board</DropdownMenuLabel>
            {boards.map((detail) => (
              <DropdownMenuItem key={detail.board.id} onSelect={() => onSwitchBoard(detail.board.id)}>
                <span className="min-w-0 flex-1 truncate">{detail.board.name}</span>
                <span
                  className="shrink-0 text-[11px] tabular-nums"
                  style={{ color: urgencyColor(detail.window, now) }}
                >
                  {remainingLabel(detail.window, now)}
                </span>
                <Check className={cn("size-3.5 shrink-0", detail.board.id !== board.board.id && "invisible")} />
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onAllBoards}>All boards</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <p className="min-w-0 truncate px-1.5 text-[14px] font-semibold tracking-tight">{title}</p>
      )}
      <span className="flex-1" />
      {onSearch ? (
        <Button variant="ghost" size="icon" aria-label="Search" onClick={onSearch}>
          <Search />
        </Button>
      ) : null}
      {actions}
      {liveOff ? (
        // A touch screen has no tooltips, so the hint is words, not a dot.
        <span
          role="status"
          className="mx-1.5 inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground"
          title={LIVE_OFF_LABEL}
          aria-label={LIVE_OFF_LABEL}
        >
          <WifiOff className="size-3.5" />
          Live off
        </span>
      ) : (
        <span
          className={cn("mx-2 size-2 shrink-0 rounded-full", live ? "animate-pulse bg-primary" : "bg-muted-foreground/30")}
          aria-label={live ? "Just synced a change" : "Watching for changes"}
        />
      )}
    </div>
  );
}
